// utils.ts
import { v4 as uuidv4 } from 'uuid';

export type FlexType =
  | 'String'
  | 'Notes'
  | 'Number'
  | 'Date'
  | 'Boolean'
  | 'Money'
  | 'Email'
  | 'Phone'
  | 'List'
  | 'Power'
  | 'Order Lines'
  | 'ImageList'
  | 'Read Only'
  | 'WebView'
  | 'Contact';

type ContactFlags = Record<string, { show: boolean; required: boolean }>;

export const contactFieldKeys = [
  'company',
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
  'phoneExtension',
  'phoneNumber2',
  'phoneExtension2',
  'address',
  'city',
  'state',
  'postalCode',
  'countryCode',
] as const;

const makeContactDefaults = (): { contactFields: ContactFlags } => ({
  contactFields: contactFieldKeys.reduce((acc, k) => {
    acc[k] = { show: false, required: false };
    return acc;
  }, {} as ContactFlags),
});

export const BASE_ALWAYS = {
  name: '', // <-- important: exists on every field
  label: '',
  heading: '',
  type: 'String' as FlexType,
  pinMappingField: 'none',
};

const REQ_DEF_KEYS = ['required', 'defaultValue'] as const;

export const TYPE_CONFIG: Record<
  FlexType,
  { defaults: Record<string, any>; keepKeys: string[] }
> = {
  String: {
    defaults: { required: false, defaultValue: '' },
    keepKeys: [...REQ_DEF_KEYS],
  },
  Notes: {
    defaults: { required: false, defaultValue: '' },
    keepKeys: [...REQ_DEF_KEYS],
  },
  Number: {
    defaults: { required: false, defaultValue: null },
    keepKeys: [...REQ_DEF_KEYS],
  },
  Date: {
    defaults: { required: false, defaultValue: null },
    keepKeys: [...REQ_DEF_KEYS],
  },
  Boolean: {
    defaults: { required: false, defaultValue: false },
    keepKeys: [...REQ_DEF_KEYS],
  },
  Money: {
    defaults: { required: false, defaultValue: null },
    keepKeys: [...REQ_DEF_KEYS],
  },
  Email: {
    defaults: { required: false, defaultValue: '' },
    keepKeys: [...REQ_DEF_KEYS],
  },
  Phone: {
    defaults: { required: false, defaultValue: '' },
    keepKeys: [...REQ_DEF_KEYS],
  },

  List: {
    defaults: {
      values: [] as Array<{ id: string; label: string }>,
      defaultValue: null,
    },
    keepKeys: ['values', 'defaultValue'],
  },
  Power: { defaults: {}, keepKeys: [] },
  'Order Lines': { defaults: {}, keepKeys: [] },
  ImageList: {
    defaults: {
      required: false,
      minRequired: 1,
      suggestedTags: [] as string[],
      suggestedImageCount: 1,
    },
    keepKeys: [
      'required',
      'minRequired',
      'suggestedTags',
      'suggestedImageCount',
    ],
  },
  'Read Only': { defaults: {}, keepKeys: [] },
  WebView: { defaults: { useLinking: false }, keepKeys: ['useLinking'] },
  Contact: { defaults: makeContactDefaults(), keepKeys: ['contactFields'] },
};

// ---------- helpers ----------
export function buildInitialValues(field: any) {
  const type: FlexType = (field?.type ?? BASE_ALWAYS.type) as FlexType;
  const typeDefaults = TYPE_CONFIG[type]?.defaults ?? {};
  return { ...BASE_ALWAYS, ...typeDefaults, ...field };
}

export function mergeForNewType(curr: any, nextType: FlexType) {
  const cfg = TYPE_CONFIG[nextType] ?? { defaults: {}, keepKeys: [] };
  return {
    ...BASE_ALWAYS,
    ...cfg.defaults,
    // preserve cross-type fields:
    name: curr.name ?? '',
    label: curr.label ?? '',
    heading: curr.heading ?? '',
    pinMappingField: curr.pinMappingField ?? 'none',
    type: nextType,
    _localKey: curr._localKey, // keep stable local key
  };
}

export function sanitizeForType(values: any) {
  const keep = new Set<string>([
    'name',
    'label',
    'heading',
    'type',
    'pinMappingField',
  ]);
  const cfg = TYPE_CONFIG[values.type as FlexType];
  (cfg?.keepKeys ?? []).forEach((k) => keep.add(k));

  const cleaned: Record<string, any> = {};
  keep.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(values, k)) {
      cleaned[k] = values[k];
    }
  });

  // Remove temporary UI fields
  delete cleaned._tagInput;

  return cleaned;
}

// stable local keys for UI/drag/select
export function seedLocalKeys(fields: any[]): any[] {
  return (fields ?? []).map((f) =>
    f?._localKey ? f : { ...f, _localKey: uuidv4() }
  );
}
export function ensureLocalKey(field: any) {
  return field?._localKey ? field : { ...field, _localKey: uuidv4() };
}
export function fieldKey(f: any) {
  return String(f?._localKey);
}

export function createNewField() {
  // default to String; name left blank for user
  return ensureLocalKey(
    buildInitialValues({
      name: '',
      label: '',
      heading: '',
      type: 'String',
    })
  );
}

export function toCamel(s: string) {
  return s
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (w, i) =>
      i === 0 ? w.toLowerCase() : w.toUpperCase()
    )
    .replace(/\s+/g, '');
}
// dropdowns you already had
export const typeSelectOptions = [
  { label: 'Text', value: 'String' },
  { label: 'Notes', value: 'Notes' },
  { label: 'Number', value: 'Number' },
  { label: 'Date', value: 'Date' },
  { label: 'Yes/No', value: 'Boolean' },
  { label: 'List', value: 'List' },
  { label: 'Money', value: 'Money' },
  { label: 'Power', value: 'Power' },
  { label: 'Order', value: 'Order Lines' },
  { label: 'Phone', value: 'Phone' },
  { label: 'Email', value: 'Email' },
  { label: 'Images', value: 'ImageList' },
  { label: 'Read Only', value: 'Read Only' },
  { label: 'Web View', value: 'WebView' },
  { label: 'Contact', value: 'Contact' },
];

export const doorFieldDataOptions = [
  { label: 'None', value: 'none' },
  { label: 'Name', value: 'name' },
  { label: 'First name', value: 'firstName' },
  { label: 'Last name', value: 'lastName' },
  { label: 'Address', value: 'address' },
  { label: 'Region (State)', value: 'region' },
  { label: 'City', value: 'city' },
  { label: 'Zip (postal Code)', value: 'postalCode' },
  { label: 'Email', value: 'email' },
  { label: 'Phone Number', value: 'phone' },
  { label: 'Cell Phone', value: 'cellPhone' },
];

export const typesMapper = {
  String: 'Text',
  Notes: 'Notes',
  Number: 'Number',
  Date: 'Date',
  Boolean: 'Yes/No',
  List: 'List',
  Money: 'Money',
  Power: 'Power',
  'Order Lines': 'Order',
  Phone: 'Phone',
  Email: 'Email',
  ImageList: 'Images',
  'Read Only': 'Read Only',
  WebView: 'Web View',
  Contact: 'Contact',
};

// small helpers
export const reorder = (list: any[], start: number, end: number) => {
  const res = Array.from(list);
  const [moved] = res.splice(start, 1);
  res.splice(end, 0, moved);
  return res;
};
export const stripPriv = (fields: any[]) =>
  fields.map(({ _localKey, ...f }) => {
    if (_localKey === 'new') {
      console.log('f', f);
    }
    return f;
  });
